import type { SelectItem } from '@talos/kyoko';
import {
  DerivativeSettlementTypeEnum,
  Field,
  FieldValidationLevel,
  MultiSelectorField,
  NumericField,
  OptionTypeEnum,
  ProductTypeEnum,
  SelectorField,
  createDateOnly,
  createDateOnlyToday,
  formattedUTCDate,
  getDisplayNameOrNameLabel,
  getDoubleMapValues,
  getOptionCoin,
  toBigWithDefault,
  type FieldValidationRule,
  type Market,
  type MarketAccount,
  type SecurityTypes,
  type StringSelectItem,
} from '@talos/kyoko';
import { immerable } from 'immer';
import type { OptionRefData } from '../../types';
import { customOptionSymbolInputToSymbol } from '../../utils';

export interface OptionModelReferenceData {
  options: Pick<
    OptionRefData,
    'options' | 'expirationByMarketByCurrencyIdentity' | 'strikesByOptionKey' | 'optionSecurityBySymbol'
  >;
  markets: { marketsByName: Map<string, Market> };
  marketAccounts: { marketAccountsByMarket: Map<string, MarketAccount[]> };
  marketsAllowingCustomOptions?: string[];
}

export const OPTION_SIDES: SelectItem<OptionTypeEnum>[] = [
  { value: OptionTypeEnum.Call, label: 'Call' },
  { value: OptionTypeEnum.Put, label: 'Put' },
];

export const SETTLEMENT_TYPES: SelectItem<DerivativeSettlementTypeEnum>[] = [
  { value: DerivativeSettlementTypeEnum.Cash, label: 'Cash' },
  { value: DerivativeSettlementTypeEnum.Physical, label: 'Physical' },
];

interface OptionData {
  isCustom: boolean;
  coinField: SelectorField<CoinSelectItem>;
  exchangeField: SelectorField<StringSelectItem>;
  marketAccountField: MultiSelectorField<StringSelectItem>;
  expiryField: SelectorField<StringSelectItem>;
  strikeField: SelectorField<StringSelectItem>;
  customExpiryField: Field<string>;
  customStrikeField: NumericField;
  typeField: SelectorField<SelectItem<OptionTypeEnum>>;
  settlementTypeField: SelectorField<SelectItem<DerivativeSettlementTypeEnum>>;
  settlementCurrencyField: SelectorField<SettlementCurrencySelectItem>;
}

export type OptionSelection =
  | { kind: 'listed'; security: SecurityTypes['Option'] }
  | {
      kind: 'custom';
      market: string;
      underlyingCode: string;
      expiration: string;
      strikePrice: string;
      optionType: OptionTypeEnum;
      derivativeSettlementType?: DerivativeSettlementTypeEnum;
      settlementCurrency?: string;
    };
export type CustomOptionSelection = Extract<OptionSelection, { kind: 'custom' }>;

// https://talostrading.atlassian.net/browse/UI-3065
export interface CoinSelectItem extends StringSelectItem {
  exchange: string;
  marketAccount: string;
  // Option coin used for option lookup maps; can be a base coin or a base/quote pair.
  underlyingCoin: string;
  // metadata used in multileg combo to filter out un-eligible symbols
  baseCurrency: string;
  quoteCurrency: string;
}

export interface SettlementCurrencySelectItem extends StringSelectItem {}

const getCoinSelectItem = (
  security: SecurityTypes['Option'],
  marketAccountsByMarket: Map<string, MarketAccount[]>
): CoinSelectItem => {
  const exchange = security.Markets?.[0] || '';
  const marketAccounts = marketAccountsByMarket.get(exchange) || [];
  const displayValue = getOptionCoin(security);

  return {
    value: displayValue,
    label: displayValue,
    exchange,
    marketAccount: marketAccounts[0]?.Name || '',
    underlyingCoin: displayValue,
    baseCurrency: security.BaseCurrency,
    quoteCurrency: security.QuoteCurrency,
  };
};

export const OPTION_DATE_FORMAT = '{dd}{Mon}{yy}';

const customOptionExpiryIsTodayOrLater: FieldValidationRule<Field<string>> = field => {
  if (!field.value) {
    return null;
  }

  const expirationDate = createDateOnly(field.value);
  if (!expirationDate || Number.isNaN(new Date(field.value).getTime()) || expirationDate < createDateOnlyToday()) {
    return {
      message: 'Expiry cannot be earlier than today',
      level: FieldValidationLevel.Error,
    };
  }

  return null;
};

const customStrikeIsPositive: FieldValidationRule<NumericField> = field => {
  if (!field.hasValue) {
    return null;
  }

  if (toBigWithDefault(field.value, 0).lte(0)) {
    return {
      message: 'Strike must be greater than 0',
      level: FieldValidationLevel.Error,
    };
  }

  return null;
};

// We need proper abstraction & encapsulation here in order to scale to multileg options later on
// We don't want to be dealing with arrays of raw fields directly in the order slice
export class Option {
  [immerable] = true;

  public static readonly type = ProductTypeEnum.Option;
  private readonly _data: OptionData;

  private constructor(
    private _referenceData: OptionModelReferenceData,
    data: OptionData
  ) {
    this._data = this.invariant(data);
    this._data.customExpiryField = this._data.customExpiryField.validate([customOptionExpiryIsTodayOrLater]);
    this._data.customStrikeField = this._data.customStrikeField.validate([customStrikeIsPositive]);
    this._data.typeField = this._data.typeField.validate([
      field => {
        if (this.isCustom) {
          // When creating custom options, either put or call are always valid
          return null;
        }

        if (!field.isDisabled && !this.security) {
          // For exchanges with settlement variants, the resolved security depends on settlement
          // type/currency — the type itself is valid, selection is just incomplete. Don't blame
          // the type field; the settlement fields convey what's still needed.
          if (this.shouldConfigureSettlement) {
            return null;
          }
          return {
            message: `Not available`,
            level: FieldValidationLevel.Error,
          };
        }
        return null;
      },
    ]);
  }

  public static createFromBlank(referenceData: OptionModelReferenceData) {
    const availableExchanges = Option.getAvailableExchanges(referenceData);
    const availableCoins = Option.getAvailableCoins(referenceData);

    const data = {
      isCustom: false,
      exchangeField: new SelectorField<StringSelectItem>({
        name: 'Exchange',
        idProperty: 'value',
        availableItems: availableExchanges,
      }),
      marketAccountField: new MultiSelectorField<StringSelectItem>({
        name: 'Market Account',
        idProperty: 'value',
      }),
      coinField: new SelectorField<CoinSelectItem>({
        name: 'Coin',
        idProperty: 'value',
        availableItems: availableCoins,
      }),
      expiryField: new SelectorField<StringSelectItem>({
        name: 'Expiry',
        idProperty: 'value',
        isDisabled: true,
      }),
      strikeField: new SelectorField<StringSelectItem>({
        name: 'Strike',
        idProperty: 'value',
        isDisabled: true,
      }),
      customExpiryField: new Field<string>({
        name: 'Expiry',
        isDisabled: true,
      }),
      customStrikeField: new NumericField({
        name: 'Strike',
        isDisabled: true,
      }),
      typeField: new SelectorField<SelectItem<OptionTypeEnum>>({
        name: 'Type',
        idProperty: 'value',
        isDisabled: true,
        availableItems: OPTION_SIDES,
        value: OPTION_SIDES[0],
      }),
      settlementTypeField: new SelectorField<SelectItem<DerivativeSettlementTypeEnum>>({
        name: 'Settlement Type',
        idProperty: 'value',
        isDisabled: true,
      }),
      settlementCurrencyField: new SelectorField<SettlementCurrencySelectItem>({
        name: 'Settlement Currency',
        idProperty: 'value',
        isDisabled: true,
      }),
    };

    return new Option(referenceData, data);
  }

  // Predicts .security ahead of a click, e.g. to disable a "prime" button that would otherwise silently no-op.
  public static canResolve(referenceData: OptionModelReferenceData, security: SecurityTypes['Option']): boolean {
    try {
      return Option.createFromSecurity(referenceData, security).security != null;
    } catch {
      return false;
    }
  }

  public static createFromSecurity(referenceData: OptionModelReferenceData, security: SecurityTypes['Option']): Option {
    const coin = getCoinSelectItem(security, referenceData.marketAccounts.marketAccountsByMarket);
    const market = security?.Markets?.[0];
    const expiration = security.Expiration;
    const strike = security.StrikePrice;

    if (!coin || !market || !expiration || !strike) {
      throw new Error(`${security.Symbol} is not an Option`);
    }

    const availableCoins = Option.getCoinsForExchange(referenceData, market);
    const availableExchanges: StringSelectItem[] = Option.getExchangesForCoin(referenceData, coin);
    const availableMarketAccounts = Option.getMarketAccountsForExchange(referenceData, market);
    const availableExpiries: StringSelectItem[] = Option.getExpiriesForExchange(referenceData, coin, market);
    const availableStrikes: StringSelectItem[] = Option.getStrikesForOption(referenceData, coin, market, expiration);
    const expiryItem = availableExpiries.find(expiry => expiry.value === expiration);
    const strikeItem = availableStrikes.find(s => s.value === strike);

    const hasSettlement = Option.exchangeHasSettlementOptions(referenceData, market);
    const settlementType = security.DerivativeSettlementType
      ? SETTLEMENT_TYPES.find(s => s.value === security.DerivativeSettlementType)
      : undefined;
    const settlementTypes = hasSettlement
      ? Option.getSettlementTypesForSecurity(referenceData, security, coin, market)
      : [];
    const settlementCurrencies = hasSettlement
      ? Option.getSettlementCurrencyOptionsForSecurity(referenceData, security, coin, market)
      : [];
    const settlementCurrency = hasSettlement
      ? Option.getSettlementCurrencyForSecurity(security, coin, settlementCurrencies)
      : undefined;

    const data = {
      isCustom: false,
      exchangeField: new SelectorField({
        name: 'Exchange',
        idProperty: 'value',
        value: availableExchanges.find(ex => ex.value === market),
        availableItems: availableExchanges,
      }),
      marketAccountField: new MultiSelectorField<StringSelectItem>({
        name: 'Market Account',
        idProperty: 'value',
        availableItems: availableMarketAccounts,
      }),
      coinField: new SelectorField({
        name: 'Coin',
        idProperty: 'value',
        value: availableCoins.find(c => c.value === coin.value),
        availableItems: availableCoins,
      }),
      expiryField: new SelectorField({
        name: 'Expiry',
        idProperty: 'value',
        value: expiryItem,
        availableItems: availableExpiries,
      }),
      strikeField: new SelectorField({
        name: 'Strike',
        idProperty: 'value',
        value: strikeItem,
        availableItems: availableStrikes,
      }),
      customExpiryField: new Field<string>({
        name: 'Expiry',
        isDisabled: true,
      }),
      customStrikeField: new NumericField({
        name: 'Strike',
        isDisabled: true,
      }),
      typeField: new SelectorField({
        name: 'Call/Put',
        idProperty: 'value',
        availableItems: OPTION_SIDES,
        value: OPTION_SIDES.find(s => s.value === security.OptionType),
      }),
      settlementTypeField: new SelectorField<SelectItem<DerivativeSettlementTypeEnum>>({
        name: 'Settlement Type',
        idProperty: 'value',
        isDisabled: !hasSettlement,
        availableItems: settlementTypes,
        value: settlementType,
        placeholder: 'Select',
      }),
      settlementCurrencyField: new SelectorField<SettlementCurrencySelectItem>({
        name: 'Settlement Currency',
        idProperty: 'value',
        isDisabled: !hasSettlement || settlementType?.value === DerivativeSettlementTypeEnum.Physical,
        availableItems: settlementCurrencies,
        value: settlementCurrency,
        placeholder: 'Select',
      }),
    };

    return new Option(referenceData, data);
  }

  public get data(): OptionData {
    return this._data;
  }

  public get referenceData(): OptionModelReferenceData {
    return this._referenceData;
  }

  public get isCustom(): boolean {
    return this._data.isCustom;
  }

  /** Returns true if the currently selected market supports building custom options */
  public get canUseCustomOptions(): boolean {
    return Option.canUseCustomOptions(this._referenceData, this._data);
  }

  /**
   * Describes whether or not the user is currently interacting with the option builder at all, trying to build an option.
   * Exchange and Coin can be selected first individaully, so once either of those two are selected, we've started being built.
   */
  public get isBeingBuilt(): boolean {
    return this._data.exchangeField.hasValue || this._data.coinField.hasValue;
  }

  /**
   * True once the selected exchange has settlement variants (e.g. STS Digital). Settlement type should
   * participate as soon as the market is selected because the market determines whether settlement
   * configuration is required.
   */
  public get shouldConfigureSettlement(): boolean {
    return Option.shouldConfigureSettlementForData(this._referenceData, this._data);
  }

  /** UI-only list for toggles; `settlementTypeField.availableItems` is the selectable subset. */
  public get allSettlementTypeOptions(): SelectItem<DerivativeSettlementTypeEnum>[] {
    return this.shouldConfigureSettlement && this._data.coinField.hasValue ? SETTLEMENT_TYPES : [];
  }

  /** UI-only list for toggles; `settlementCurrencyField.availableItems` is the selectable subset. */
  public get allSettlementCurrencyOptions(): SettlementCurrencySelectItem[] {
    if (!this.shouldConfigureSettlement) {
      return [];
    }

    // The toggle shows both currencies even when only one is selectable for the current contract.
    return [this._data.coinField.value?.baseCurrency, this._data.coinField.value?.quoteCurrency]
      .filter((currency): currency is string => currency != null)
      .map(currency => ({ value: currency, label: currency }));
  }

  public get security(): SecurityTypes['Option'] | undefined {
    if (this.isCustom) {
      return undefined;
    }

    const {
      coinField,
      exchangeField,
      strikeField,
      expiryField,
      typeField,
      settlementTypeField,
      settlementCurrencyField,
    } = this.data;
    if (
      !coinField.hasValue ||
      !exchangeField.hasValue ||
      !expiryField.hasValue ||
      !strikeField.hasValue ||
      !typeField.hasValue
    ) {
      return undefined;
    }

    return this._referenceData.options.options.find(option => {
      const coin = getOptionCoin(option);
      if (
        coin !== coinField.value!.value ||
        !option.Markets?.includes(exchangeField.value!.value) ||
        option.Expiration !== expiryField.value!.value ||
        option.StrikePrice !== strikeField.value!.value ||
        option.OptionType !== typeField.value!.value
      ) {
        return false;
      }

      // For settlement exchanges (e.g. STS Digital), the contract above isn't enough — the same
      // coin/expiry/strike/type has several settlement variants. Cash variants are disambiguated by
      // SettlementCurrency (base vs quote); Physical locks to the base currency. We deliberately do NOT
      // match on SettleValueType: every tradable variant is Regular (the Inverted variant the original
      // spec described was rejected by the orderbook). Assumes one Cash variant per SettlementCurrency.
      if (this.shouldConfigureSettlement) {
        if (!settlementTypeField.hasValue || option.DerivativeSettlementType !== settlementTypeField.value!.value) {
          return false;
        }
        if (!settlementCurrencyField.hasValue) {
          return false;
        }
        if (settlementTypeField.value!.value !== DerivativeSettlementTypeEnum.Physical) {
          return option.SettlementCurrency === settlementCurrencyField.value!.value;
        }
      }

      return true;
    });
  }

  /** Grabs the values from all the fields, and returns the derived string symbol from those selections */
  public get customOptionSymbol(): string | undefined {
    const selection = this.resolvedCustomSelection;
    return selection ? customOptionSymbolInputToSymbol(selection) : undefined;
  }

  /**
   * If the Option model is currently in custom mode, and the custom properties set completely line up with an already
   * existing Option Security, this getter returns that Security for you to use instead.
   */
  public get existingCustomOptionSecurity(): SecurityTypes['Option'] | undefined {
    if (!this.isCustom) {
      return undefined;
    }

    const symbol = this.customOptionSymbol;
    return symbol ? this._referenceData.options.optionSecurityBySymbol?.get(symbol) : undefined;
  }

  public get selection(): OptionSelection | undefined {
    if (!this.isCustom) {
      const security = this.security;
      return security ? { kind: 'listed', security } : undefined;
    }

    return this.customSelection;
  }

  public get customSelection(): CustomOptionSelection | undefined {
    if (!this.isCustom) {
      return undefined;
    }

    return this.resolvedCustomSelection;
  }

  private get resolvedCustomSelection(): CustomOptionSelection | undefined {
    const {
      coinField,
      exchangeField,
      customExpiryField,
      customStrikeField,
      typeField,
      settlementTypeField,
      settlementCurrencyField,
    } = this.data;

    // Make sure all the required properties of a custom option are selected
    if (
      !coinField.hasValue ||
      !exchangeField.hasValue ||
      !customExpiryField.hasValue ||
      customExpiryField.hasInvalidValue ||
      !customStrikeField.hasValue ||
      customStrikeField.hasInvalidValue ||
      !typeField.hasValue
    ) {
      return undefined;
    }

    // If the properties we have selected tell us that we also require a settlement options, make sure they are populated too
    if (this.shouldConfigureSettlement) {
      if (!settlementTypeField.hasValue || !settlementCurrencyField.hasValue) {
        return undefined;
      }
    }

    return {
      kind: 'custom',
      market: exchangeField.value!.value,
      underlyingCode: coinField.value!.value,
      expiration: customExpiryField.value!,
      strikePrice: customStrikeField.value!,
      optionType: typeField.value!.value,
      derivativeSettlementType: settlementTypeField.value?.value,
      settlementCurrency: settlementCurrencyField.value?.value,
    };
  }

  public updateCoin = (coin: CoinSelectItem | undefined) => {
    const newData = {
      ...this._data,
      coinField: this._data.coinField.updateValue(coin),
      exchangeField: this._data.exchangeField.updateAvailableItems(
        coin ? Option.getExchangesForCoin(this._referenceData, coin) : Option.getAvailableExchanges(this._referenceData)
      ),
      ...this.clearedSettlementFields(),
    };
    return this.updateData(newData);
  };

  public updateExchange = (exchange: StringSelectItem | undefined) => {
    const newData = {
      ...this._data,
      exchangeField: this._data.exchangeField.updateValue(exchange),
      coinField: this._data.coinField.updateAvailableItems(
        exchange
          ? Option.getCoinsForExchange(this._referenceData, exchange.value)
          : Option.getAvailableCoins(this._referenceData)
      ),
      ...this.clearedSettlementFields(),
    };

    if (this.isCustom && !Option.canUseCustomOptions(this._referenceData, newData)) {
      // Changing market can make custom mode invalid. In that case, fall back to listed mode and clear fields
      return this.updateData({
        ...newData,
        isCustom: false,
        ...Option.clearedModeSelectionFields(newData),
      });
    }

    return this.updateData(newData);
  };

  public updateMarketAccount = (marketAccount: StringSelectItem[] | undefined) => {
    const newData = {
      ...this._data,
      marketAccountField: this._data.marketAccountField.updateValue(marketAccount),
    };
    return this.updateData(newData);
  };

  public updateExpiry = (expiry?: StringSelectItem) => {
    const newData = {
      ...this._data,
      expiryField: this._data.expiryField.updateValue(expiry),
      ...this.clearedSettlementFields(),
    };
    return this.updateData(newData);
  };

  public updateStrike = (strike?: StringSelectItem) => {
    const newData = {
      ...this._data,
      strikeField: this._data.strikeField.updateValue(strike),
      ...this.clearedSettlementFields(),
    };
    return this.updateData(newData);
  };

  public updateIsCustom = (nextIsCustom: boolean) => {
    if (nextIsCustom === this.isCustom) {
      return this;
    }

    if (!nextIsCustom) {
      // If we're going from custom -> listed, and the custom properties set exactly match an existing security, we switch to that security!
      if (this.existingCustomOptionSecurity) {
        return Option.createFromSecurity(this._referenceData, this.existingCustomOptionSecurity);
      } else {
        // Custom -> listed starts a fresh listed build, but keeps market and coin so the user does not
        // need to re-enter the top-level option context.
        return this.updateData({
          ...this._data,
          isCustom: false,
          ...Option.clearedModeSelectionFields(this._data),
        });
      }
    }

    // Else down here we are flipping from listed to custom. We keep all the properties the user entered in listed mode,
    // so below here we are just flipping mode and then moving properties from listed strike+expiry -> custom strike+expiry
    return this.updateData({
      ...this._data,
      isCustom: true,
      customExpiryField: this._data.customExpiryField.updateValue(
        listedExpiryValueToCustomExpiryValue(this._data.expiryField.value?.value),
        true
      ),
      customStrikeField: this._data.customStrikeField.updateValue(this._data.strikeField.value?.value, true),
    });
  };

  public updateCustomExpiry = (expiry?: string) => {
    const newData = {
      ...this._data,
      customExpiryField: this._data.customExpiryField.updateValue(expiry),
    };
    return this.updateData(newData);
  };

  public updateCustomStrike = (strike?: string) => {
    const newData = {
      ...this._data,
      customStrikeField: this._data.customStrikeField.updateValue(strike),
    };
    return this.updateData(newData);
  };

  public updateType = (type?: SelectItem<OptionTypeEnum>) => {
    const newData = {
      ...this._data,
      typeField: this._data.typeField.updateValue(type),
      // When switching between put and call, we only need to clear the later settlement fields if we are looking at listed options, not custom
      ...(this.isCustom ? {} : this.clearedSettlementFields()),
    };
    return this.updateData(newData);
  };

  public updateSettlementType = (settlementType?: SelectItem<DerivativeSettlementTypeEnum>) => {
    const newData = {
      ...this._data,
      settlementTypeField: this._data.settlementTypeField.updateValue(settlementType),
      // Clear currency on type change — Physical auto-selects it, Cash requires explicit user choice
      settlementCurrencyField: this._data.settlementCurrencyField.updateValue(undefined),
    };
    return this.updateData(newData);
  };

  public updateSettlementCurrency = (settlementCurrency?: SettlementCurrencySelectItem) => {
    const newData = {
      ...this._data,
      settlementCurrencyField: this._data.settlementCurrencyField.updateValue(settlementCurrency),
    };
    return this.updateData(newData);
  };

  public getType(): ProductTypeEnum {
    return Option.type;
  }

  /** Changing any contract dimension invalidates a prior settlement choice — clear it so the user re-picks. */
  private clearedSettlementFields(): Pick<OptionData, 'settlementTypeField' | 'settlementCurrencyField'> {
    return {
      settlementTypeField: this._data.settlementTypeField.updateValue(undefined),
      settlementCurrencyField: this._data.settlementCurrencyField.updateValue(undefined),
    };
  }

  // When switching from custom to listed, clear mode-specific selections but keep market and coin.
  private static clearedModeSelectionFields(data: OptionData) {
    return {
      expiryField: data.expiryField.updateValue(undefined),
      strikeField: data.strikeField.updateValue(undefined),
      customExpiryField: data.customExpiryField.updateValue(undefined),
      customStrikeField: data.customStrikeField.updateValue(undefined),
      typeField: data.typeField.updateValue(OPTION_SIDES[0]),
      settlementTypeField: data.settlementTypeField.updateValue(undefined),
      settlementCurrencyField: data.settlementCurrencyField.updateValue(undefined),
    };
  }

  private updateData(data: Partial<OptionData>): Option {
    const newData = {
      ...this._data,
      ...data,
    };
    return new Option(this._referenceData, newData);
  }

  private invariant(data: OptionData): OptionData {
    const { isCustom } = data;
    // Rebuild all derived field state from the current raw selections. Each update starts from a
    // conservative disabled state, then re-enables only the fields that are valid for the current path.
    const updated = {
      ...data,
      expiryField: data.expiryField.setDisabled(true),
      strikeField: data.strikeField.setDisabled(true),
      customExpiryField: data.customExpiryField.setDisabled(true),
      customStrikeField: data.customStrikeField.setDisabled(true),
      typeField: data.typeField.setDisabled(true),
      settlementTypeField: data.settlementTypeField.setDisabled(true),
      settlementCurrencyField: data.settlementCurrencyField.setDisabled(true),
    };

    // Market + coin are the base selection for both listed and custom options. From there, listed
    // options can choose an expiry from refdata; custom options can directly enter expiry/strike/type.
    if (updated.exchangeField.hasValue && updated.coinField.hasValue) {
      if (isCustom) {
        updated.customExpiryField = updated.customExpiryField.setDisabled(false);
        updated.customStrikeField = updated.customStrikeField.setDisabled(false);
        updated.typeField = updated.typeField.setDisabled(false);
      } else {
        updated.expiryField = updated.expiryField
          .setDisabled(false)
          .updateAvailableItems(
            Option.getExpiriesForExchange(
              this._referenceData,
              updated.coinField.value!,
              updated.exchangeField.value!.value
            )
          );
      }
      updated.marketAccountField = updated.marketAccountField
        .setDisabled(false)
        .updateAvailableItems(
          Option.getMarketAccountsForExchange(this._referenceData, updated.exchangeField.value!.value)
        );
    }

    // Listed options need a selected expiry before strikes can be derived from refdata. Custom options
    // skip this because their strike is free-form and was enabled in the market + coin block above.
    if (!isCustom && updated.exchangeField.hasValue && updated.coinField.hasValue && updated.expiryField.hasValue) {
      updated.strikeField = updated.strikeField
        .setDisabled(false)
        .updateAvailableItems(
          Option.getStrikesForOption(
            this._referenceData,
            updated.coinField.value!,
            updated.exchangeField.value!.value,
            updated.expiryField.value!.value
          )
        );
      updated.typeField = updated.typeField.setDisabled(false);
    }

    // Settlement configuration is market-driven. If the selected market has settlement variants, keep the
    // type/currency fields in sync with the current option shape; otherwise clear stale settlement choices.
    if (updated.exchangeField.hasValue) {
      const shouldConfigureSettlement = Option.shouldConfigureSettlementForData(this._referenceData, updated);

      if (shouldConfigureSettlement) {
        // Settlement exchanges expose Type + Currency. The selectable sets are derived from the
        // contract variants in ref data. updateAvailableItems keeps a valid value, clears an invalid
        // value, and defaults only when exactly one required value is available.

        // ---- Settlement type ----
        const canSelectSettlementType = Option.canSelectSettlementType(updated, isCustom);
        const selectableSettlementTypes = canSelectSettlementType
          ? Option.getSettlementTypesForSelection(this._referenceData, updated, isCustom)
          : [];

        updated.settlementTypeField = updated.settlementTypeField
          .setDisabled(!canSelectSettlementType || selectableSettlementTypes.length <= 1)
          .updateAvailableItems(selectableSettlementTypes);

        // ---- Settlement currency ----
        const settlementType = updated.settlementTypeField.value?.value;
        if (settlementType == null) {
          // No settlement type, which we are dependent on -- clear settlement currency
          updated.settlementCurrencyField = updated.settlementCurrencyField.setDisabled(true).updateAvailableItems([]);
        } else {
          const selectableSettlementCurrencies = Option.getSettlementCurrenciesForSelection(
            this._referenceData,
            updated,
            settlementType,
            isCustom
          );

          if (settlementType === DerivativeSettlementTypeEnum.Physical) {
            // Physical settles in the base currency — lock the field to it.
            const baseCurrencyItem = selectableSettlementCurrencies.find(
              c => c.value === updated.coinField.value?.baseCurrency
            );
            updated.settlementCurrencyField = updated.settlementCurrencyField
              .setDisabled(true)
              .updateAvailableItems(selectableSettlementCurrencies)
              .updateValue(baseCurrencyItem);
          } else {
            updated.settlementCurrencyField = updated.settlementCurrencyField
              .setDisabled(selectableSettlementCurrencies.length <= 1)
              .updateAvailableItems(selectableSettlementCurrencies);
          }
        }
      } else {
        // Exchange doesn't use settlement variants — clear both fields
        updated.settlementTypeField = updated.settlementTypeField
          .setDisabled(true)
          .updateAvailableItems([])
          .updateValue(undefined);
        updated.settlementCurrencyField = updated.settlementCurrencyField
          .setDisabled(true)
          .updateAvailableItems([])
          .updateValue(undefined);
      }
    }

    return updated;
  }

  // Static because invariant() builds the next OptionData from a draft `data` before an Option wraps it
  // (no `this` to hang instance methods on); also reused by RFQ selectors operating over option.data.
  public static optionHasCompleteContractSelection(data: OptionData, isCustom = data.isCustom): boolean {
    return (
      data.coinField.hasValue &&
      data.exchangeField.hasValue &&
      (isCustom ? data.customExpiryField.hasValue : data.expiryField.hasValue) &&
      (isCustom ? data.customStrikeField.hasValue : data.strikeField.hasValue) &&
      data.typeField.hasValue
    );
  }

  private static canSelectSettlementType(data: OptionData, isCustom = data.isCustom): boolean {
    // Different requirements to make settlement type selectable (not disabled) based on custom vs not custom
    return isCustom
      ? data.exchangeField.hasValue && data.coinField.hasValue
      : Option.optionHasCompleteContractSelection(data, isCustom);
  }

  private static shouldConfigureSettlementForData(referenceData: OptionModelReferenceData, data: OptionData): boolean {
    const exchange = data.exchangeField.value?.value;
    return exchange != null && Option.exchangeHasSettlementOptions(referenceData, exchange);
  }

  public static exchangeHasSettlementOptions(referenceData: OptionModelReferenceData, exchange: string): boolean {
    return referenceData.options.options.some(
      opt => opt.Markets?.includes(exchange) && opt.DerivativeSettlementType != null
    );
  }

  // Checks if the selected market supports custom option building
  private static canUseCustomOptions(referenceData: OptionModelReferenceData, data: OptionData): boolean {
    const marketsAllowingCustomOptions = referenceData.marketsAllowingCustomOptions;
    const selectedMarket = data.exchangeField.value?.value;
    return marketsAllowingCustomOptions != null && marketsAllowingCustomOptions.includes(selectedMarket ?? '');
  }

  private static getOptionsMatchingContract(
    referenceData: OptionModelReferenceData,
    data: OptionData
  ): SecurityTypes['Option'][] {
    return Option.getOptionsMatchingContractValues(referenceData, {
      coin: data.coinField.value?.value,
      exchange: data.exchangeField.value?.value,
      expiration: data.expiryField.value?.value,
      strike: data.strikeField.value?.value,
      optionType: data.typeField.value?.value,
    });
  }

  private static getOptionsMatchingContractValues(
    referenceData: OptionModelReferenceData,
    contract: {
      coin?: string;
      exchange?: string;
      expiration?: string;
      strike?: string;
      optionType?: OptionTypeEnum;
    }
  ): SecurityTypes['Option'][] {
    return referenceData.options.options.filter(option => {
      const coin = getOptionCoin(option);
      return (
        (contract.coin == null || coin === contract.coin) &&
        (contract.exchange == null || option.Markets?.includes(contract.exchange)) &&
        (contract.expiration == null || option.Expiration === contract.expiration) &&
        (contract.strike == null || option.StrikePrice === contract.strike) &&
        (contract.optionType == null || option.OptionType === contract.optionType)
      );
    });
  }

  private static getSettlementTypesForSelection(
    referenceData: OptionModelReferenceData,
    data: OptionData,
    isCustom: boolean
  ): SelectItem<DerivativeSettlementTypeEnum>[] {
    if (isCustom) {
      // For custom, always show all possible
      return SETTLEMENT_TYPES;
    }

    // For listed, show the available types in the set of listed options
    const availableTypes = new Set(
      Option.getOptionsMatchingContract(referenceData, data).map(option => option.DerivativeSettlementType)
    );
    return SETTLEMENT_TYPES.filter(type => availableTypes.has(type.value));
  }

  private static getSettlementCurrenciesForSelection(
    referenceData: OptionModelReferenceData,
    data: OptionData,
    settlementType: DerivativeSettlementTypeEnum | undefined,
    isCustom: boolean
  ): SettlementCurrencySelectItem[] {
    if (isCustom) {
      // For custom option building, we always show either just base if its physical, else base+quote if its cash
      const currencies =
        settlementType === DerivativeSettlementTypeEnum.Physical
          ? [data.coinField.value?.baseCurrency]
          : [data.coinField.value?.baseCurrency, data.coinField.value?.quoteCurrency];

      return currencies
        .filter((currency): currency is string => currency != null)
        .map(currency => ({ value: currency, label: currency }));
    }

    // Below for listed, the settlement currencies we show are based on what listed options contain in the selectable set
    const currencies = new Set<string>();
    for (const option of Option.getOptionsMatchingContract(referenceData, data)) {
      if (settlementType != null && option.DerivativeSettlementType !== settlementType) {
        continue;
      }
      if (settlementType === DerivativeSettlementTypeEnum.Physical) {
        const baseCurrency = data.coinField.value?.baseCurrency;
        if (baseCurrency) {
          currencies.add(baseCurrency);
        }
        continue;
      }

      const currency = option.SettlementCurrency;
      if (currency) {
        currencies.add(currency);
      }
    }

    return [data.coinField.value?.baseCurrency, data.coinField.value?.quoteCurrency]
      .filter((currency): currency is string => currency != null && currencies.has(currency))
      .map(currency => ({ value: currency, label: currency }));
  }

  private static getSettlementTypesForSecurity(
    referenceData: OptionModelReferenceData,
    security: SecurityTypes['Option'],
    coin: CoinSelectItem,
    exchange: string
  ): SelectItem<DerivativeSettlementTypeEnum>[] {
    const availableTypes = new Set(
      Option.getOptionsMatchingSecurityContract(referenceData, security, coin, exchange).map(
        option => option.DerivativeSettlementType
      )
    );
    return SETTLEMENT_TYPES.filter(type => availableTypes.has(type.value));
  }

  private static getSettlementCurrencyForSecurity(
    security: SecurityTypes['Option'],
    coin: CoinSelectItem,
    settlementCurrencies: SettlementCurrencySelectItem[]
  ): SettlementCurrencySelectItem | undefined {
    const settlementCurrency =
      security.DerivativeSettlementType === DerivativeSettlementTypeEnum.Physical
        ? coin.baseCurrency
        : security.SettlementCurrency;
    return settlementCurrencies.find(c => c.value === settlementCurrency);
  }

  private static getSettlementCurrencyOptionsForSecurity(
    referenceData: OptionModelReferenceData,
    security: SecurityTypes['Option'],
    coin: CoinSelectItem,
    exchange: string
  ): SettlementCurrencySelectItem[] {
    if (security.DerivativeSettlementType === DerivativeSettlementTypeEnum.Physical) {
      return coin.baseCurrency ? [{ value: coin.baseCurrency, label: coin.baseCurrency }] : [];
    }

    const currencies = new Set(
      Option.getOptionsMatchingSecurityContract(referenceData, security, coin, exchange)
        .filter(option => option.DerivativeSettlementType === DerivativeSettlementTypeEnum.Cash)
        .map(option => option.SettlementCurrency)
    );

    return [coin.baseCurrency, coin.quoteCurrency]
      .filter((currency): currency is string => currency != null && currencies.has(currency))
      .map(currency => ({ value: currency, label: currency }));
  }

  private static getOptionsMatchingSecurityContract(
    referenceData: OptionModelReferenceData,
    security: SecurityTypes['Option'],
    coin: CoinSelectItem,
    exchange: string
  ): SecurityTypes['Option'][] {
    return Option.getOptionsMatchingContractValues(referenceData, {
      coin: coin.value,
      exchange,
      expiration: security.Expiration,
      strike: security.StrikePrice,
      optionType: security.OptionType,
    });
  }

  private static getAvailableExchanges(referenceData: OptionModelReferenceData): StringSelectItem[] {
    const options = referenceData.options.options;
    const markets = options.map(o => o.Markets || []).flat();

    return Array.from(new Set(markets)).map(ex => ({
      label: referenceData.markets.marketsByName.get(ex)?.DisplayName || ex,
      value: ex,
    }));
  }

  private static getAvailableCoins(referenceData: OptionModelReferenceData): CoinSelectItem[] {
    const coins = referenceData.options.options.reduce((acc, option) => {
      const coinSelectItem = getCoinSelectItem(option, referenceData.marketAccounts.marketAccountsByMarket);
      return acc.set(coinSelectItem.value, coinSelectItem);
    }, new Map());

    return Array.from(coins.values());
  }

  private static getCoinsForExchange(referenceData: OptionModelReferenceData, exchange: string): CoinSelectItem[] {
    if (!exchange) {
      return Option.getAvailableCoins(referenceData);
    }

    const options = referenceData.options.options || [];
    const relevantCoins = options.filter(o => o.Markets?.[0] === exchange);

    const coins = relevantCoins.reduce((acc, option) => {
      const coinSelectItem = getCoinSelectItem(option, referenceData.marketAccounts.marketAccountsByMarket);
      return acc.set(coinSelectItem.value, coinSelectItem);
    }, new Map<string, CoinSelectItem>());

    return Array.from(coins.values());
  }

  private static getExchangesForCoin(
    referenceData: OptionModelReferenceData,
    coin: CoinSelectItem
  ): StringSelectItem[] {
    if (!coin) {
      return Option.getAvailableExchanges(referenceData);
    }

    const expirationByMarket = referenceData.options.expirationByMarketByCurrencyIdentity.get(coin.underlyingCoin)!;
    const allCoins = referenceData.options.options.map(security =>
      getCoinSelectItem(security, referenceData.marketAccounts.marketAccountsByMarket)
    );

    return Array.from(expirationByMarket.keys())
      .filter(exchange => {
        const exchangesSupportingCoin = allCoins.filter(c => c.value === coin.value).map(c => c.exchange);
        return exchangesSupportingCoin.includes(exchange);
      })
      .map(ex => ({
        label: referenceData.markets.marketsByName.get(ex)?.DisplayName || ex,
        value: ex,
      }));
  }

  private static getExpiriesForExchange(
    referenceData: OptionModelReferenceData,
    coin: CoinSelectItem,
    market: string
  ): StringSelectItem[] {
    const expirationByMarket = referenceData.options.expirationByMarketByCurrencyIdentity.get(coin.underlyingCoin)!;
    const expirations = expirationByMarket?.get(market) || new Set();

    return Array.from(expirations.values())
      .toSorted((a, b) => new Date(a).valueOf() - new Date(b).valueOf())
      .map(ex => ({
        label: formattedUTCDate(new Date(ex), OPTION_DATE_FORMAT),
        value: ex,
      }));
  }

  private static getMarketAccountsForExchange(
    referenceData: OptionModelReferenceData,
    market: string
  ): StringSelectItem[] {
    const marketAccounts = referenceData.marketAccounts.marketAccountsByMarket.get(market) || [];

    return marketAccounts.map(ma => ({
      label: getDisplayNameOrNameLabel(ma),
      value: ma.Name,
    }));
  }

  private static getStrikesForOption(
    referenceData: OptionModelReferenceData,
    coin: CoinSelectItem,
    market: string,
    expiration: string
  ): StringSelectItem[] {
    const strikesByKey = referenceData.options.strikesByOptionKey!;
    const optionKey = `${coin.underlyingCoin}/${market}/${expiration}`;

    const uniqueStrikes = new Set(getDoubleMapValues(strikesByKey, optionKey));
    return (
      Array.from(uniqueStrikes.values())
        .toSorted((a, b) => toBigWithDefault(a, 0).cmp(b))
        .map(strike => ({ value: strike, label: toBigWithDefault(strike, 0).toFixed() })) || []
    );
  }
}

export function listedExpiryValueToCustomExpiryValue(listedExpiryValue: string | undefined): string | undefined {
  if (!listedExpiryValue) {
    return undefined;
  }

  // Listed option expiries are timestamp strings from ref data, while custom option RFQs use DateOnly.
  const listedExpiryDateOnly = createDateOnly(listedExpiryValue);
  return listedExpiryDateOnly ?? undefined;
}
